import React, { useState } from "react";
import { ArrowLeft, Library, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { TPL_CHIP, docApi, docErr, docPref, isPmc, isSa } from "@/lib/docApi";
import { Empty, ErrorBox, Loading, StatusFilter, SummaryChip, btnGold, btnRed, useLoad } from "@/components/nds/windows/finance/parts";
import { History, StatusChip } from "@/components/nds/windows/doc/parts";
import TemplateForm from "@/components/nds/windows/doc/TemplateForm";

function TemplateDetail({ rec, pmc, onBack, onEdit, onArchived }) {
  const archive = async () => {
    try { await docApi.post(`/templates/${rec.id}/archive`); toast.success("Template archived"); onArchived(); } catch (e) { toast.error(docErr(e)); }
  };
  return (
    <div data-testid="doc-tpl-detail" className="p-5 space-y-4">
      <button type="button" onClick={onBack} data-testid="doc-tpl-back" className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-nex-gold"><ArrowLeft className="h-3 w-3" /> All templates</button>
      <div className="flex flex-wrap items-center gap-3">
        <h3 data-testid="doc-tpl-detail-name" className="text-lg text-nex-silver font-semibold">{rec.template_name}</h3>
        <StatusChip map={TPL_CHIP} status={rec.status} testid="doc-tpl-detail-status" />
        {pmc && rec.status === "active" && <div className="ml-auto flex gap-2">
          <button type="button" onClick={onEdit} data-testid="doc-tpl-edit" className={btnGold}>Edit</button>
          <button type="button" onClick={archive} data-testid="doc-tpl-archive" className={btnRed}>Archive</button>
        </div>}
      </div>
      <p className="text-sm text-nex-muted">{rec.document_type} · {rec.org_header ? "TEA letterhead" : "No letterhead"} · {rec.description}</p>
      <div className="text-xs text-nex-muted">Fields: {rec.variable_fields.map((f) => `${f.label}${f.required ? "*" : ""}`).join(", ") || "none"}</div>
      <pre data-testid="doc-tpl-detail-prompt" className="whitespace-pre-wrap text-xs text-white/80 bg-black/30 border border-white/10 p-3 rounded-sm">{rec.base_prompt}</pre>
      <History rec={rec} testid="doc-tpl-history" />
    </div>
  );
}

export default function DocTemplateLibraryContent() {
  const { user } = useAuth();
  const pmc = isPmc(user);
  const [status, setStatusState] = useState(() => docPref.get("tpl_status", "active"));
  const setStatus = (v) => { docPref.set("tpl_status", v); setStatusState(v); };
  const [view, setView] = useState(null);
  const [state, load] = useLoad(() => Promise.all([docApi.get("/templates"), docApi.get("/config")]).then(([t, c]) => ({ ...t, config: c })), []);
  if (state.loading && !state.data) return <Loading testid="doc-tpl-loading" />;
  if (state.error) return <ErrorBox testid="doc-tpl-error" error={state.error} onRetry={load} />;
  const { templates, config } = state.data;
  const pick = (id) => templates.find((t) => t.id === id);
  if (view?.mode === "form") {
    return <TemplateForm rec={view.id && pick(view.id)} config={config} isSa={isSa(user)} onCancel={() => setView(null)}
      onTypesChanged={() => load()} onSaved={(d) => { load(); setView({ mode: "detail", id: d.id }); }} />;
  }
  if (view?.mode === "detail" && pick(view.id)) {
    return <TemplateDetail rec={pick(view.id)} pmc={pmc} onBack={() => setView(null)} onEdit={() => setView({ mode: "form", id: view.id })} onArchived={load} />;
  }
  const counts = templates.reduce((a, t) => ({ ...a, [t.status]: (a[t.status] || 0) + 1 }), { all: templates.length });
  const list = templates.filter((t) => status === "all" || t.status === status);
  return (
    <div data-testid="doc-tpl" className="flex flex-col h-full">
      <div className="px-5 py-3 border-b border-white/10 flex flex-wrap items-center gap-3">
        <Library className="h-4 w-4 text-nex-gold" />
        <SummaryChip tone="green" testid="doc-tpl-summary">{counts.active || 0} active templates</SummaryChip>
        <StatusFilter prefix="doc-tpl" chips={TPL_CHIP} value={status} onChange={setStatus} counts={counts} />
        {pmc && <button type="button" onClick={() => setView({ mode: "form" })} data-testid="doc-tpl-new" className={`${btnGold} ml-auto inline-flex items-center gap-1`}><Plus className="h-3 w-3" /> New template</button>}
      </div>
      <ul data-testid="doc-tpl-list" className="flex-1 overflow-y-auto divide-y divide-white/5">
        {list.map((t) => (
          <li key={t.id}>
            <button type="button" onClick={() => setView({ mode: "detail", id: t.id })} data-testid={`doc-tpl-row-${t.id}`} className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-nex-gold/5">
              <span className="flex-1 min-w-0 truncate text-nex-silver font-medium">{t.template_name}</span>
              <span className="text-xs text-nex-muted">{t.document_type}</span>
              <StatusChip map={TPL_CHIP} status={t.status} />
            </button>
          </li>
        ))}
        {!list.length && <li><Empty testid="doc-tpl-empty">No templates here yet.{pmc ? " Create one to start generating documents." : ""}</Empty></li>}
      </ul>
    </div>
  );
}
